import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';

export interface DiseaseNode {
  label: string;
  labelPosition: string;
  color: string | undefined;
}

export interface DiseaseLink {
  from: string;
  to: string;
  color: string | undefined;
  value: number;
}

export interface GenderRow {
  no_of_conditions: number;
  male_count: number;
  female_count: number;
  total_members: number;
}

export interface CostRow {
  no_of_conditions: number;
  total_cost: number;
}
export interface AgeGroupRow {
  no_of_conditions: number;
  '0-18': number;
  '19-26': number;
  '27-40': number;
  '41-64': number;
  '65+': number;
}

export type BucketKey = 1 | 2 | 3 | 4 | '5+';
export interface BucketValue {
  male: number;
  female: number;
  cost: number;
}
export interface AgeBucketValue {
  '0-18': number;
  '19-26': number;
  '27-40': number;
  '41-64': number;
  '65+': number;
  cost: number;
}

function getRandomColor() {
  const arr = [
    patientAccessTheme.palette.primary[500],
    patientAccessTheme.palette.primary[300],
    patientAccessTheme.palette.primary[200],
  ];
  const randomIndex = Math.floor(Math.random() * arr.length);
  const item = arr[randomIndex];
  return item;
}

export const formatPieChartData = (data: any) => {
  const formattedData = [
    {
      value: '0',
      color: patientAccessTheme.palette.primary[300],
    },
    {
      value: '0',
      color: patientAccessTheme.palette.primary[100],
    },
  ];
  if (data.length) {
    formattedData[0].value = data
      ?.find((val: any) => val?.label === 'Yes')
      ?.value.toFixed(2);
    formattedData[1].value = data
      ?.find((val: any) => val?.label === 'No')
      ?.value.toFixed(2);
  }
  return formattedData;
};

export const formatPieChartLegend = (data: any) => {
  const formattedLegend = [
    {
      label: 'Yes',
      value: 0,
      color: patientAccessTheme.palette.primary[300],
    },
    {
      label: 'No',
      value: 0,
      color: patientAccessTheme.palette.primary[100],
    },
  ];
  if (data.length) {
    formattedLegend[0].value = data
      ?.find((val: any) => val?.label === 'Yes')
      ?.value.toFixed(2);
    formattedLegend[1].value = data
      ?.find((val: any) => val?.label === 'No')
      ?.value.toFixed(2);
  }
  return formattedLegend;
};

export const formatMemberDistAgeData = (data: any) => {
  const formattedData = [
    {
      seriesname: 'Male',
      plottooltext: 'Male - $dataValue',
      data: [],
      color: patientAccessTheme.palette.primary[400],
    },
    {
      seriesname: 'Female',
      plottooltext: 'Female - $dataValue',
      data: [],
      color: patientAccessTheme.palette.primary[200],
    },
  ];
  let label = [];
  if (data?.memberDistribution?.length) {
    formattedData[0].data = data.memberDistribution.map((item: any) => {
      return {
        value: item.maleCount,
      };
    });
    formattedData[1].data = data.memberDistribution.map((item: any) => {
      return {
        value: item.femaleCount,
      };
    });
    label = data.memberDistribution.map((item: any) => {
      return {
        label: item.ageGroup,
      };
    });
  }
  const rightLabeledValues = [
    { title: 'Average Age', value: Math.ceil(data.avgAge) },
    { title: 'Male', value: data.totalMale },
    { title: 'Female', value: data.totalFemale },
    { title: 'Other', value: data.totalOther },
  ];
  return {
    formattedData: formattedData,
    xAxis: label,
    rightLabeledValues: rightLabeledValues,
  };
};

export const formatHccMemberDistAgeData = (data: any) => {
  const formattedData = [
    {
      seriesname: 'Low',
      plottooltext: 'Low - $dataValue',
      data: [],
      color: patientAccessTheme.palette.primary[400],
    },
    {
      seriesname: 'Medium',
      plottooltext: 'Medium - $dataValue',
      data: [],
      color: '#55CE9B',
    },
    {
      seriesname: 'High',
      plottooltext: 'High - $dataValue',
      data: [],
      color: patientAccessTheme.palette.riskStratificationCard?.row2Color,
    },
  ];
  let label = [];
  if (data.length) {
    formattedData[0].data = data.map((item: any) => {
      return {
        value: item.lowCount,
      };
    });
    formattedData[1].data = data.map((item: any) => {
      return {
        value: item.mediumCount,
      };
    });
    formattedData[2].data = data.map((item: any) => {
      return {
        value: item.highCount,
      };
    });
    label = data.map((item: any) => {
      return {
        label: item.ageGroup,
      };
    });
  }
  return {
    formattedData: formattedData,
    xAxis: label,
  };
};

export const formatDiseaseNode = (data: any, condition: string) => {
  const node: DiseaseNode[] = [];

  node.push({
    label: condition,
    labelPosition: 'start',
    color: patientAccessTheme.palette.primary[500],
  });

  data?.map((item: any) => {
    const eachNodeValue: DiseaseNode = {
      label: '',
      labelPosition: 'end',
      color: getRandomColor(),
    };
    eachNodeValue.label = item.diseaseName;
    node.push(eachNodeValue);
  });
  return node;
};
export const formatDiseaseLink = (data: any, condition: string) => {
  const link: DiseaseLink[] = [];

  data?.map((item: any) => {
    const eachValue: DiseaseLink = {
      from: condition,
      to: '',
      color: getRandomColor(),
      value: 26,
    };

    eachValue.to = item.diseaseName;
    eachValue.value = item.percent;
    link.push(eachValue);
  });
  return link;
};

export const addSortedPrevalence = (data: any) => {
  const sortedPrevalence: any = [];

  data?.prevalenceSummary?.map((diseaseItem: any) => {
    diseaseItem?.individualDiseases?.map((item: any) => {
      sortedPrevalence.push({
        ...item,
        mainMeasure: {
          totalCost: diseaseItem?.totalCost,
          rxCost: diseaseItem?.rxCost,
          medicalCost: diseaseItem?.medicalCost,
        },
      });
    });
  });

  sortedPrevalence.sort((a: any, b: any) => b.totalCost - a.totalCost);

  data.sortedPrevalence = sortedPrevalence;

  return data;
};

type AnyRecord = Record<string, any>;

export function mergeByKey<T extends AnyRecord, U extends AnyRecord>(
  baseArray: T[],
  mergeArray: U[],
  key: keyof T & keyof U,
  mergeFieldName = 'total_cost',
  defaultValue: number = 0,
): Array<T & { [key: string]: number }> {
  const mergeMap = new Map<any, U>();

  mergeArray.forEach((item) => {
    mergeMap.set(item[key], item);
  });

  return baseArray.map((item) => ({
    ...item,
    [mergeFieldName]: mergeMap.get(item[key])?.[mergeFieldName] ?? defaultValue,
  }));
}

export const buildPrevalenceByGenderGraphData = (mergedData: any[]) => {
  const buckets: Record<BucketKey, BucketValue> = {
    1: { male: 0, female: 0, cost: 0 },
    2: { male: 0, female: 0, cost: 0 },
    3: { male: 0, female: 0, cost: 0 },
    4: { male: 0, female: 0, cost: 0 },
    '5+': { male: 0, female: 0, cost: 0 },
  };

  mergedData.forEach((item) => {
    const key: BucketKey =
      item.no_of_conditions >= 5
        ? '5+'
        : (item.no_of_conditions as 1 | 2 | 3 | 4);

    buckets[key].male += item.male_count;
    buckets[key].female += item.female_count;
    buckets[key].cost += item.total_cost;
  });

  return {
    maleData: Object.values(buckets).map((b) => ({ value: b.male })),
    femaleData: Object.values(buckets).map((b) => ({ value: b.female })),
    costData: Object.values(buckets).map((b) => ({
      value: Number(b.cost.toFixed(2)),
    })),
  };
};

export const buildPrevalenceByAgeGroupGraphData = (mergedData: any[]) => {
  const buckets: Record<BucketKey, AgeBucketValue> = {
    1: { '0-18': 0, '19-26': 0, '27-40': 0, '41-64': 0, '65+': 0, cost: 0 },
    2: { '0-18': 0, '19-26': 0, '27-40': 0, '41-64': 0, '65+': 0, cost: 0 },
    3: { '0-18': 0, '19-26': 0, '27-40': 0, '41-64': 0, '65+': 0, cost: 0 },
    4: { '0-18': 0, '19-26': 0, '27-40': 0, '41-64': 0, '65+': 0, cost: 0 },
    '5+': { '0-18': 0, '19-26': 0, '27-40': 0, '41-64': 0, '65+': 0, cost: 0 },
  };

  mergedData.forEach((item) => {
    const key: BucketKey =
      item.no_of_conditions >= 5
        ? '5+'
        : (item.no_of_conditions as 1 | 2 | 3 | 4);

    buckets[key]['0-18'] += item['0-18'] || 0;
    buckets[key]['19-26'] += item['19-26'] || 0;
    buckets[key]['27-40'] += item['27-40'] || 0;
    buckets[key]['41-64'] += item['41-64'] || 0;
    buckets[key]['65+'] += item['65+'] || 0;
    buckets[key].cost += item.total_cost || 0;
  });

  return {
    age0to18Data: Object.values(buckets).map((b) => ({ value: b['0-18'] })),
    age19to26Data: Object.values(buckets).map((b) => ({ value: b['19-26'] })),
    age27to40Data: Object.values(buckets).map((b) => ({ value: b['27-40'] })),
    age41to64Data: Object.values(buckets).map((b) => ({ value: b['41-64'] })),
    age65PlusData: Object.values(buckets).map((b) => ({ value: b['65+'] })),
    costData: Object.values(buckets).map((b) => ({
      value: Number(b.cost.toFixed(2)),
    })),
  };
};

export const getPageUrl = (type: string) => {
  switch (type) {
    case 'disease':
      return 'disease-prelavence';
    case 'chronic':
      return 'chronic-prelavence';
    case 'stoploss':
      return 'stoploss-triggers';
    default:
      return;
  }
};
